const SKIP = ".no-print, button:not([data-pdf='stat']), input, select, textarea, form, nav, svg, script, style, [role='dialog'], [aria-hidden='true'], [data-pdf-skip], [data-testid='pdf-viewer-overlay'], kbd";
const INLINE = new Set(["SPAN", "B", "I", "EM", "STRONG", "A", "CODE", "SMALL", "SUP", "SUB", "MARK", "TIME", "ABBR"]);

const clean = (t) => String(t || "").replace(/\s+/g, " ").trim();
const visible = (el) => el.getClientRects().length > 0;

const isLeaf = (el) => [...el.children].every((c) => INLINE.has(c.tagName) || c.tagName === "BR" || c.tagName === "SVG");

function readTable(tbl) {
  const headRow = tbl.tHead?.rows?.[0] || (tbl.rows[0]?.querySelector("th") ? tbl.rows[0] : null);
  const headers = headRow ? [...headRow.cells].map((c) => clean(c.innerText)) : [];
  const rows = [];
  const bodies = tbl.tBodies?.length ? [...tbl.tBodies] : [tbl];
  for (const tb of bodies) {
    for (const r of tb.rows) {
      if (r === headRow || r.closest("thead")) continue;
      const cells = [...r.cells].map((c) => clean(c.innerText));
      if (cells.some(Boolean)) rows.push(cells);
    }
  }
  return { type: "table", headers, rows };
}

export function extractPageBlocks(root) {
  const blocks = [];
  let pendingKv = null;
  const flushKv = () => { if (pendingKv?.items.length) blocks.push(pendingKv); pendingKv = null; };
  const walk = (el) => {
    if (el.nodeType !== 1) return;
    if (el.matches(SKIP) || !visible(el)) return;
    if (el.matches("[data-pdf='stat']")) {
      const value = clean(el.querySelector("[data-pdf='stat-value']")?.innerText ?? el.children[0]?.innerText);
      const label = clean(el.querySelector("[data-pdf='stat-label']")?.innerText ?? el.children[1]?.innerText);
      if (label || value) { pendingKv = pendingKv || { type: "kv", items: [] }; pendingKv.items.push([label, value]); }
      return;
    }
    if (/^H[1-4]$/.test(el.tagName)) {
      flushKv();
      const text = clean(el.innerText);
      if (text) blocks.push({ type: "heading", level: Math.min(Number(el.tagName[1]), 3), text });
      return;
    }
    if (el.tagName === "TABLE") { flushKv(); blocks.push(readTable(el)); return; }
    if (el.tagName === "P" || (isLeaf(el) && clean(el.innerText))) {
      flushKv();
      const text = clean(el.innerText);
      if (text && text !== blocks[blocks.length - 1]?.text) blocks.push({ type: "text", text });
      return;
    }
    [...el.children].forEach(walk);
  };
  walk(root);
  flushKv();
  return blocks.slice(0, 300);
}

export function pageTitleFrom(root) {
  const h = root?.querySelector("h1, h2");
  return clean(h?.innerText) || document.title || "CJMS Page";
}

export function openPdfFromBlocks({ title, blocks, publicMode = false, sourceUrl }) {
  const landscape = blocks.some((b) => b.type === "table" && (b.headers?.length || b.rows?.[0]?.length || 0) >= 7);
  const url = publicMode ? "/public/portal/page-pdf" : "/ujms/print/page-pdf";
  const caseNumber = clean(document.querySelector("[data-testid='case-header-number']")?.innerText) || null;
  window.dispatchEvent(new CustomEvent("ujms:open-pdf", {
    detail: { url, title: `${title} — PDF`, body: { title, source_url: sourceUrl ?? window.location.pathname, blocks, landscape, case_number: caseNumber } },
  }));
}

export function exportElementPdf(root, { title, publicMode } = {}) {
  const el = root || document.getElementById("ujms-main") || document.body;
  const blocks = extractPageBlocks(el);
  openPdfFromBlocks({ title: title || pageTitleFrom(el), blocks, publicMode });
  return blocks.length;
}
